import { BackfaceVisibility, TransformStyle } from "@kuika/kuika-cl-model";
import React from "react";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSlider from "../../../../ui-kit/kuika-slider/kuika-slider";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import OriginGrid from "../origin-grid/origin-grid";
import "./transform-settings-popover.scss";

export interface TransformSettingsValue {
  transformOriginX?: number;
  transformOriginY?: number;
  backfaceVisibility?: BackfaceVisibility;
  transformStyle?: TransformStyle;
  perspective?: number;
  childrenPerspective?: number;
  childrenPerspectiveOriginX?: number;
  childrenPerspectiveOriginY?: number;
}

export interface TransformSettingsPopoverContentProps {
  value: TransformSettingsValue;
  onChange: (patch: Partial<TransformSettingsValue>) => void;
  disabled?: boolean;
  dataTestId?: string;
}

const BACKFACE_TAB_OPTIONS: { value: BackfaceVisibility; label: string }[] = [
  { value: "visible", label: "Visible" },
  { value: "hidden", label: "Hidden" }
];

const TRANSFORM_STYLE_OPTIONS: { value: TransformStyle; label: string }[] = [
  { value: "flat", label: "Flat" },
  { value: "preserve-3d", label: "Preserve 3D" }
];

const PERSPECTIVE_MIN: number = 0;
const PERSPECTIVE_MAX: number = 2000;
const PERSPECTIVE_STEP: number = 10;
const ORIGIN_MIN: number = 0;
const ORIGIN_MAX: number = 100;

/**
 * TransformSettingsPopover (content body) — rendered inside a KuikaPopover
 * anchored to the ⚙ button in the TransformAccordion header. The parent
 * component owns visibility (controlled KuikaPopover) and the data; this
 * component is a pure presentational form.
 *
 * Sections: transform-origin (OriginGrid + Left/Top % inputs), Backface
 * (KuikaTabs), Transform Style (KuikaSelectbox), Self perspective
 * (KuikaSlider), Children perspective (distance KuikaSlider + 2nd OriginGrid).
 */
const TransformSettingsPopoverContent: React.FC<TransformSettingsPopoverContentProps> = ({
  value,
  onChange,
  disabled,
  dataTestId
}) => {
  const handleOriginChange = (
    xKey: "transformOriginX" | "childrenPerspectiveOriginX",
    yKey: "transformOriginY" | "childrenPerspectiveOriginY",
    next: { x: number; y: number }
  ): void => {
    onChange({ [xKey]: next.x, [yKey]: next.y } as Partial<TransformSettingsValue>);
  };

  const handlePercentInput = (key: keyof TransformSettingsValue, raw: string | number): void => {
    const num: number = typeof raw === "number" ? raw : Number(raw);
    if (Number.isNaN(num)) return;
    const clamped: number = Math.min(ORIGIN_MAX, Math.max(ORIGIN_MIN, num));
    onChange({ [key]: clamped } as Partial<TransformSettingsValue>);
  };

  return (
    <div className="transform-settings" data-testid={dataTestId}>
      {/* Origin */}
      <section className="transform-settings__section">
        <h4 className="transform-settings__section-title">Origin</h4>
        <div className="transform-settings__origin-row">
          <OriginGrid
            value={{
              x: value.transformOriginX ?? 50,
              y: value.transformOriginY ?? 50
            }}
            onChange={(next: { x: number; y: number }): void =>
              handleOriginChange("transformOriginX", "transformOriginY", next)
            }
            disabled={disabled}
            dataTestId={dataTestId ? `${dataTestId}-origin-grid` : undefined}
          />
          <div className="transform-settings__origin-inputs">
            <label className="transform-settings__input-label">
              <span>Left</span>
              <KuikaInput
                type="number"
                size="sm"
                value={value.transformOriginX ?? 50}
                rightUnit="%"
                min={ORIGIN_MIN}
                max={ORIGIN_MAX}
                disabled={disabled}
                onChange={(_e, next: string | number): void => handlePercentInput("transformOriginX", next)}
                data-testid={dataTestId ? `${dataTestId}-origin-left` : undefined}
              />
            </label>
            <label className="transform-settings__input-label">
              <span>Top</span>
              <KuikaInput
                type="number"
                size="sm"
                value={value.transformOriginY ?? 50}
                rightUnit="%"
                min={ORIGIN_MIN}
                max={ORIGIN_MAX}
                disabled={disabled}
                onChange={(_e, next: string | number): void => handlePercentInput("transformOriginY", next)}
                data-testid={dataTestId ? `${dataTestId}-origin-top` : undefined}
              />
            </label>
          </div>
        </div>
      </section>

      {/* Backface */}
      <section className="transform-settings__section">
        <h4 className="transform-settings__section-title">Backface</h4>
        <KuikaTabs
          options={BACKFACE_TAB_OPTIONS}
          value={value.backfaceVisibility ?? "visible"}
          onChange={(next: string): void => onChange({ backfaceVisibility: next as BackfaceVisibility })}
          size="sm"
          disabled={disabled}
          dataTestId={dataTestId ? `${dataTestId}-backface-tabs` : undefined}
        />
      </section>

      {/* Transform Style */}
      <section className="transform-settings__section">
        <h4 className="transform-settings__section-title">Style</h4>
        <KuikaSelectbox
          options={TRANSFORM_STYLE_OPTIONS}
          value={value.transformStyle ?? "flat"}
          onChange={(next: string | number | (string | number)[] | null | any): void => {
            if (typeof next === "string") {
              onChange({ transformStyle: next as TransformStyle });
            }
          }}
          size="sm"
          disabled={disabled}
          dataTestId={dataTestId ? `${dataTestId}-style-select` : undefined}
        />
      </section>

      {/* Self perspective */}
      <section className="transform-settings__section">
        <h4 className="transform-settings__section-title">Self perspective</h4>
        <div className="transform-settings__row">
          <span className="transform-settings__row-label">Distance</span>
          <KuikaSlider
            value={value.perspective ?? 0}
            onChange={(next: number): void => onChange({ perspective: next })}
            min={PERSPECTIVE_MIN}
            max={PERSPECTIVE_MAX}
            step={PERSPECTIVE_STEP}
            precision={0}
            unit="px"
            size="sm"
            disabled={disabled}
            dataTestId={dataTestId ? `${dataTestId}-perspective` : undefined}
          />
        </div>
      </section>

      {/* Children perspective */}
      <section className="transform-settings__section">
        <h4 className="transform-settings__section-title">Children perspective</h4>
        <div className="transform-settings__row">
          <span className="transform-settings__row-label">Distance</span>
          <KuikaSlider
            value={value.childrenPerspective ?? 0}
            onChange={(next: number): void => onChange({ childrenPerspective: next })}
            min={PERSPECTIVE_MIN}
            max={PERSPECTIVE_MAX}
            step={PERSPECTIVE_STEP}
            precision={0}
            unit="px"
            size="sm"
            disabled={disabled}
            dataTestId={dataTestId ? `${dataTestId}-children-perspective` : undefined}
          />
        </div>
        <div className="transform-settings__origin-row">
          <OriginGrid
            value={{
              x: value.childrenPerspectiveOriginX ?? 50,
              y: value.childrenPerspectiveOriginY ?? 50
            }}
            onChange={(next: { x: number; y: number }): void =>
              handleOriginChange("childrenPerspectiveOriginX", "childrenPerspectiveOriginY", next)
            }
            disabled={disabled}
            dataTestId={dataTestId ? `${dataTestId}-children-origin-grid` : undefined}
          />
          <div className="transform-settings__origin-inputs">
            <label className="transform-settings__input-label">
              <span>Left</span>
              <KuikaInput
                type="number"
                size="sm"
                value={value.childrenPerspectiveOriginX ?? 50}
                rightUnit="%"
                min={ORIGIN_MIN}
                max={ORIGIN_MAX}
                disabled={disabled}
                onChange={(_e, next: string | number): void => handlePercentInput("childrenPerspectiveOriginX", next)}
                data-testid={dataTestId ? `${dataTestId}-children-origin-left` : undefined}
              />
            </label>
            <label className="transform-settings__input-label">
              <span>Top</span>
              <KuikaInput
                type="number"
                size="sm"
                value={value.childrenPerspectiveOriginY ?? 50}
                rightUnit="%"
                min={ORIGIN_MIN}
                max={ORIGIN_MAX}
                disabled={disabled}
                onChange={(_e, next: string | number): void => handlePercentInput("childrenPerspectiveOriginY", next)}
                data-testid={dataTestId ? `${dataTestId}-children-origin-top` : undefined}
              />
            </label>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TransformSettingsPopoverContent;
